// ============================================================================
// 🔐 AUTH STORE
//    💡 Store de autenticación y sesión:
//       - Login estándar y modo kiosko
//       - Almacenamiento del token JWT, usuario y permisos
//       - Restauración y cambio de contraseña
//       - Persistencia de sesión en localStorage
// ============================================================================

import { defineStore } from "pinia";
import type { PersistenceOptions } from "pinia-plugin-persistedstate";
import { getEndpoints } from "@/services/apiServices/endpoints";
import { fetchPostData } from "@/services/apiServices/http";
import { usePushNotifications } from "@/composables/pwa/usePushNotifications";

// 🎯 Tipado del estado de autenticación
interface IAuthState {
  error: string;
  jwt: string;
  user: string;
  remember: boolean;
  userShortName: string;
  userId: number | null;
  userLocalId: number | null;
  userPic: string;
  userPermissions: Record<string, boolean>;
  msgResetPassword: string;
  msgUpdatingPassword: string;
}

function getAuthErrorMessage(err: any): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.data?.message ||
    err?.message ||
    "No se ha podido iniciar sesión. Revisa tus credenciales o la configuración del servidor."
  );
}

function normalizeAuthErrorMessage(err: unknown): string {
  if (typeof err === "string" && err.trim()) return err;

  const error = err as any;
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  const validationErrors = responseData?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors)
      .flat()
      .find((message) => typeof message === "string" && message.trim());

    if (firstError) return String(firstError);
  }

  return (
    responseData?.message ||
    responseData?.error ||
    responseData?.title ||
    error?.data?.message ||
    error?.message ||
    "No se ha podido iniciar sesion. Revisa tus credenciales o la configuracion del servidor."
  );
}

export const useAuthStore = defineStore("auth", {
  // 📦 Estado inicial
  state: (): IAuthState => ({
    error: "",
    jwt: "",
    user: "",
    remember: JSON.parse(localStorage.getItem("rememberSetup") || "false"),
    userShortName: "",
    userId: null,
    userLocalId: null,
    userPic: "",
    userPermissions: {},
    msgResetPassword: "",
    msgUpdatingPassword: "",
  }),

  // 🔍 Getters
  getters: {
    getJwt: (state) => state.jwt,
    getUser: (state) => state.user,
    getError: (state) => state.error,
    getUserShortName: (state) => state.userShortName,
    getUserId: (state) => state.userId,
    getUserLocalId: (state) => state.userLocalId,
    getUserPic: (state) => state.userPic,
    getMsgResetPassword: (state) => state.msgResetPassword,
    getMsgUpdatingPassword: (state) => state.msgUpdatingPassword,
    getUserPermissions: (state) => state.userPermissions,
    getRemember: (state) => state.remember,
  },

  // ⚙️ Acciones
  actions: {
    /**
     * 🔐 Login estándar con usuario y contraseña
     */
    async login(
      user: string,
      company: string,
      password: string,
      rememberMe: boolean,
      isCustomer: boolean,
    ): Promise<boolean> {
      const URL = getEndpoints().login;
      const credentials = {
        username: user,
        claveEmpresa: company,
        password,
        esCliente: isCustomer,
      };

      try {
        const data = await fetchPostData<any>(URL, "", credentials);
        console.log("user", data);

        if (!data.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.user = data.empleado.nombre;
        this.userShortName = data.empleado.nombreCorto;
        this.userId = data.empleado.id;
        this.userLocalId = data.empleado.idLocal;
        this.userPic = data.empleado.foto;

        const permisosApp = data.empleado?.permisosApp ?? {};

        // 🧾 Mapeo de permisos según tipo de usuario

        // si es cliente solo ve SofyGerencia
        if (isCustomer) {
          this.userPermissions = {
            permisoConsultaInicio: true,
            permisoConsultaAgenda: false,
            permisoConsultaFichajes: false,
            permisoConsultaGastos: false,
            permisoConsultaClientes: false,
            permisoConsultaPedidos: false,
            permisoConsultaTransporte: false,
            permisoConsultaMensajeria: false,
            permisoConsultaLocalizador: false,
            permisoColsultaIntervenciones: false,
            permisoConsultaGerencia: true,
            permisoConsultaOrdenesTrabajo: true,
          };
        } else {
          this.userPermissions = {
            permisoConsultaInicio: true,
            permisoConsultaAgenda: !!permisosApp.tieneAppAgenda,
            permisoConsultaFichajes: !!permisosApp.tieneAppFichajes,
            permisoConsultaGastos: !!permisosApp.tieneAppGastos,
            permisoConsultaClientes: false,
            permisoConsultaPedidos: false,
            permisoConsultaTransporte: false,
            permisoConsultaMensajeria: !!permisosApp.tieneAppMensajeria,
            permisoConsultaLocalizador: !!permisosApp.tieneAppLocalizador,
            permisoColsultaIntervenciones: !!permisosApp.tieneAppIntervenciones,
            permisoConsultaGerencia: !!permisosApp.tieneAppGerencia,
          };
        }

        this.remember = rememberMe;
        this.error = "";
        return true;
      } catch (err: any) {
        console.error("Error en login:", err);
        this.error = normalizeAuthErrorMessage(err);
        return false;
      }
    },

    /**
     * 🔢 Login por PIN (EAN tarjeta) — SofyApps
     *    ✅ No toca kioskLogin (otra PWA)
     *    Payload: { eanTarjeta, claveEmpresa }
     *    Response: igual que login normal (token + empleado + permisosApp)
     */
    async loginPin(
      eanTarjeta: string,
      claveEmpresa: string,
      rememberMe: boolean,
    ): Promise<boolean> {
      const URL = getEndpoints().loginPin; // ✅ endpoint NUEVO de SofyApps
      const credentials = { eanTarjeta, claveEmpresa };

      try {
        const data = await fetchPostData<any>(URL, "", credentials);

        if (!data?.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.user = data.empleado?.nombre ?? "";
        this.userShortName = data.empleado?.nombreCorto ?? "";
        this.userId =
          typeof data.empleado?.id === "number" ? data.empleado.id : null;
        this.userPic = data.empleado?.foto ?? "";

        // ✅ Igual que login normal: permisos completos
        this.userPermissions = {
          permisoConsultaInicio: true,
          permisoConsultaAgenda: !!data.empleado?.permisosApp?.tieneAppAgenda,
          permisoConsultaFichajes:
            !!data.empleado?.permisosApp?.tieneAppFichajes,
          permisoConsultaGastos: !!data.empleado?.permisosApp?.tieneAppGastos,
          permisoConsultaClientes: false,
          permisoConsultaPedidos: false,
          permisoConsultaTransporte: false,
          permisoConsultaMensajeria:
            !!data.empleado?.permisosApp?.tieneAppMensajeria,
          permisoConsultaLocalizador:
            !!data.empleado?.permisosApp?.tieneAppLocalizador,
          permisoColsultaIntervenciones:
            !!data.empleado?.permisosApp?.tieneAppIntervenciones,
          permisoConsultaGerencia:
            !!data.empleado?.permisosApp?.tieneAppGerencia,
        };

        this.remember = rememberMe;
        this.error = "";
        return true;
      } catch (err: any) {
        console.error("Error en login por PIN:", err);
        this.error = normalizeAuthErrorMessage(err);
        return false;
      }
    },

    /**
     * 🔐 Login automático de cliente desde aplicación externa/escritorio
     *    Payload: { authToken }
     *    Response: { token, empleado }
     */
    async loginClienteByToken(authToken: string): Promise<boolean> {
      const URL = getEndpoints().loginCliente;

      try {
        const data = await fetchPostData<any>(URL, "", {
          authToken,
        });

        if (!data?.token) {
          this.error = "No se ha recibido token de acceso";
          return false;
        }

        this.jwt = data.token;
        this.user = data.empleado?.nombre ?? "";
        this.userShortName = data.empleado?.nombreCorto ?? "";
        this.userId =
          typeof data.empleado?.id === "number" ? data.empleado.id : null;
        this.userLocalId =
          typeof data.empleado?.idLocal === "number"
            ? data.empleado.idLocal
            : null;
        this.userPic = data.empleado?.foto ?? "";

        this.userPermissions = {
          permisoConsultaInicio: true,
          permisoConsultaAgenda: false,
          permisoConsultaFichajes: false,
          permisoConsultaGastos: false,
          permisoConsultaClientes: false,
          permisoConsultaPedidos: true,
          permisoConsultaTransporte: false,
          permisoConsultaMensajeria: false,
          permisoConsultaLocalizador: false,
          permisoColsultaIntervenciones: false,
          permisoConsultaGerencia:
            !!data.empleado?.permisosApp?.tieneAppGerencia,
        };

        this.remember = false;
        this.error = "";

        localStorage.setItem("token", data.token);
        localStorage.setItem("name", this.user);

        return true;
      } catch (err: any) {
        console.error("Error en login automático de cliente:", err);
        this.error = normalizeAuthErrorMessage(err);
        return false;
      }
    },
    /* async loginClienteByToken(authToken: string): Promise<boolean> {
      console.log("Mock authToken:", authToken);

      this.jwt = "mock-token-cliente";
      this.user = "Cliente Demo";
      this.userShortName = "Cliente";
      this.userId = 1;
      this.userLocalId = 1;
      this.userPic = "";
      this.userPermissions = {
        permisoConsultaInicio: true,
        permisoConsultaAgenda: false,
        permisoConsultaFichajes: false,
        permisoConsultaGastos: false,
        permisoConsultaClientes: false,
        permisoConsultaPedidos: true,
        permisoConsultaTransporte: false,
        permisoConsultaMensajeria: false,
        permisoConsultaLocalizador: false,
        permisoColsultaIntervenciones: false,
        permisoConsultaGerencia: true,
      };

      localStorage.setItem("token", this.jwt);
      localStorage.setItem("name", this.user);

      return true;
    }, */

    /**
     * 🚪 Logout del usuario, limpieza total del estado
     */
    async logOut() {
      const URL = getEndpoints().logout;
      const token = this.jwt;

      const { unsubscribe } = usePushNotifications();

      try {
        // 🔔 eliminar suscripción push si existe
        await unsubscribe().catch((err) => {
          console.warn("No se pudo eliminar la suscripción push", err);
        });

        // 🔐 avisar al backend del logout
        if (token) {
          await fetchPostData(URL, token, {});
        }
      } catch (err) {
        console.warn(
          "No se pudo notificar el cierre de sesión al servidor",
          err,
        );
      }

      // 🔄 Limpieza del estado
      this.jwt = "";
      this.user = "";
      this.userId = null;
      this.userPic = "";
      this.userShortName = "";
      this.userPermissions = {};
      this.remember = false;

      // 🔁 Restauración parcial de valores en localStorage
      const apiURL = localStorage.getItem("apiURL");
      const apiFichajesURL =
        localStorage.getItem("apiFichajesURL") ||
        sessionStorage.getItem("apiFichajesURL");
      const company = localStorage.getItem("company");
      const rememberSetup = localStorage.getItem("rememberSetup");

      localStorage.clear();
      sessionStorage.clear();

      if (apiURL) localStorage.setItem("apiURL", apiURL);
      if (apiFichajesURL)
        localStorage.setItem("apiFichajesURL", apiFichajesURL);
      if (company) localStorage.setItem("company", company);
      if (rememberSetup) localStorage.setItem("rememberSetup", rememberSetup);

      return true;
    },

    /**
     * 🧪 Setter manual de token (útil para restaurar sesión desde persistencia)
     */
    setJwt(token: string) {
      this.jwt = token;
    },

    /**
     * 🔁 Solicita envío de enlace de restauración de contraseña
     */
    async restorePassword(user: string, company: string): Promise<boolean> {
      const URL = getEndpoints().restorePassword;
      try {
        await fetchPostData<any>(URL, "", {
          login: user,
          claveEmpresa: company,
        });
        this.msgResetPassword =
          "Se ha enviado un enlace para restablecer su contraseña.";
        return true;
      } catch (err) {
        this.error = normalizeAuthErrorMessage(err);
        return false;
      }
    },

    /**
     * 🔐 Actualización de contraseña tras recuperación
     */
    async passwordUpdating(
      token: string,
      newPassword: string,
      newPasswordRepeat: string,
    ): Promise<boolean> {
      const URL = getEndpoints().passwordUpdating;
      try {
        const data = await fetchPostData<any>(URL, "", {
          password: newPassword,
          passwordConfirmed: newPasswordRepeat,
          tokenRecovery: token,
        });

        this.msgUpdatingPassword = data.message;
        return true;
      } catch (err) {
        this.error = normalizeAuthErrorMessage(err);
        return false;
      }
    },
  },

  // 💾 Persistencia de estado entre recargas
  persist: {
    enabled: true,
    strategies: [
      {
        key: "auth",
        storage: localStorage,
        paths: [
          "jwt",
          "user",
          "remember",
          "userShortName",
          "userId",
          "userPic",
          "userPermissions",
        ],
      },
    ],
  } as PersistenceOptions,
});
